// Shailesh AI: ask the store anything, by typing or by voice. The same chat sits behind a button on every screen,
// on the register and as a tab of the phone tools. The server decides what each role may look up.
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, loadSession } from '../lib/api';
import { ErrorNote, Icon } from './ui';

interface Msg { role: 'user' | 'assistant'; text: string }

const SUGGESTIONS: Record<string, string[]> = {
  cashier: ["What's the price of Tito's 1.75?", 'Red for beef stew, under $25', 'Something like Meiomi but cheaper', 'A light beer for a party'],
  manager: ["What's the price of Tito's 1.75?", 'How are sales today vs last week?', 'Who is working this weekend?', 'How many cartons of Marlboro Gold should I order?'],
  owner: ['How many cartons of Marlboro Gold should I order?', 'Compare our last 3 Christmases', 'Profit and loss for last month', 'How much did we pay Casey this year?', 'What time is Morgan coming in tomorrow?'],
};

// One conversation per person, kept while the tab is open, so the button on each screen picks up where it left off.
const storeKey = () => `njpos.shailesh.${loadSession()?.user.id ?? 'anon'}`;
const loadMsgs = (): Msg[] => { try { return JSON.parse(sessionStorage.getItem(storeKey()) ?? '[]'); } catch { return []; } };
const saveMsgs = (m: Msg[]) => { try { sessionStorage.setItem(storeKey(), JSON.stringify(m.slice(-30))); } catch { /* storage blocked */ } };

/** Voice typing where the browser has it (Chrome, Edge, Safari on iPhone). */
export function useVoice(onText: (t: string, final: boolean) => void) {
  const Rec = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
  const [listening, setListening] = useState(false);
  const rec = useRef<any>(null);
  useEffect(() => () => rec.current?.abort?.(), []);
  function start() {
    if (!Rec || listening) return;
    const r = new Rec();
    r.lang = 'en-US'; r.interimResults = true; r.maxAlternatives = 1;
    r.onresult = (e: any) => {
      const res = e.results[e.results.length - 1];
      onText(Array.from(e.results as ArrayLike<any>).map((x: any) => x[0].transcript).join(''), res.isFinal);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    rec.current = r; setListening(true); r.start();
  }
  return { supported: !!Rec, listening, start, stop: () => rec.current?.stop() };
}

/** Plain text with **bold** and line breaks, the way the AI writes. */
function Answer({ text }: { text: string }) {
  return <>{text.split('\n').map((line, i) => (
    <Fragment key={i}>{i > 0 && <br />}{line.split(/(\*\*[^*]+\*\*)/g).map((part, j) => (part.startsWith('**') && part.endsWith('**') ? <b key={j}>{part.slice(2, -2)}</b> : part.replace(/^#+\s*/, '')))}</Fragment>
  ))}</>;
}

export function ShaileshChat({ page, header }: { page?: string; header?: ReactNode }) {
  const role = loadSession()?.user.role ?? 'cashier';
  const [msgs, setMsgs] = useState<Msg[]>(loadMsgs);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const voice = useVoice((t, final) => { setQ(t); if (final) void send(t); });
  useEffect(() => { saveMsgs(msgs); end.current?.scrollIntoView({ block: 'end' }); }, [msgs, busy]);

  async function send(text = q) {
    const question = text.trim();
    if (!question || busy) return;
    setErr(null); setQ(''); setBusy(true);
    const history = msgs.slice(-10);
    setMsgs((m) => [...m, { role: 'user', text: question }]);
    try {
      const r = await api<{ answer: string }>('/api/shailesh/ask', { body: { question, history, page } });
      setMsgs((m) => [...m, { role: 'assistant', text: r.answer || 'I could not find an answer to that.' }]);
    } catch (e) { setErr(e); setQ(question); setMsgs((m) => m.slice(0, -1)); }
    finally { setBusy(false); input.current?.focus(); }
  }

  return (
    <div className="ai-chat">
      {header}
      <div className="ai-msgs" aria-live="polite">
        {msgs.length === 0 && (
          <div className="ai-hello">
            <div className="ai-badge"><Icon name="sparkle" size={22} /></div>
            <b>Hi, I'm Shailesh AI.</b>
            <p className="faint">Ask me a price, for a wine, beer or liquor pick from what's on the shelf{role !== 'cashier' ? ', or anything about the store' : ''}.</p>
            <div className="ai-suggest">{(SUGGESTIONS[role] ?? SUGGESTIONS.cashier!).map((s) => <button key={s} onClick={() => send(s)}>{s}</button>)}</div>
          </div>
        )}
        {msgs.map((m, i) => <div key={i} className={`ai-msg ${m.role}`}>{m.role === 'assistant' ? <Answer text={m.text} /> : m.text}</div>)}
        {busy && <div className="ai-msg assistant ai-typing" aria-label="Shailesh AI is thinking"><span /><span /><span /></div>}
        <div ref={end} />
      </div>
      <ErrorNote error={err} />
      <form className="ai-input" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <textarea ref={input} rows={1} value={q} placeholder={voice.listening ? 'Listening…' : 'Ask Shailesh AI…'} aria-label="Ask Shailesh AI" maxLength={1000}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
        {voice.supported && (
          <button type="button" className={`ai-mic${voice.listening ? ' on' : ''}`} onClick={voice.listening ? voice.stop : voice.start} aria-label={voice.listening ? 'Stop listening' : 'Ask by voice'} title="Ask by voice">
            <Icon name="mic" />
          </button>
        )}
        <button className="primary" disabled={busy || !q.trim()} aria-label="Send"><Icon name="send" /></button>
      </form>
      {msgs.length > 0 && <button className="ghost ai-clear" onClick={() => { setMsgs([]); setErr(null); }}>New conversation</button>}
    </div>
  );
}

/** The Shailesh AI button and its panel. Floating in the corner, or a plain button for a toolbar. */
export function ShaileshButton({ page, inline }: { page?: string; inline?: boolean }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <>
      <button className={inline ? 'ai-inline-btn' : 'ai-fab'} onClick={() => setOpen((o) => !o)} aria-expanded={open} title="Ask Shailesh AI">
        <Icon name="sparkle" size={inline ? 16 : 20} /><span>Shailesh AI</span>
      </button>
      {open && (
        <div className="ai-panel" role="dialog" aria-label="Shailesh AI">
          <ShaileshChat page={page} header={
            <header className="ai-head"><Icon name="sparkle" /><b className="grow">Shailesh AI</b><button className="ghost" onClick={() => setOpen(false)} aria-label="Close"><Icon name="x" size={16} /></button></header>
          } />
        </div>
      )}
    </>
  );
}
